import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import { Box, CircularProgress, Grid, Typography } from "@mui/material";
import { toast } from "react-toastify";
import { TOContext } from "context/TContext";
import { useCreditCard } from "hooks/useCreditCard";
import useAdyenPayment from "hooks/useAdyenPayment";
import TOCardPayment from "components/Payments/TOCardPayment";
import PaymentDivider from "components/Shared/PaymentDivider";
import { useTipInfoSettingsContext } from "context/TipInfoSettingsContext";
import { useCompleteAdyenSessionMutation } from "api";
import {
  CashPaymentSection,
  PaymentHeaderSection,
  PaymentMethodIcons,
  PaymentTypeHeader,
  useCommonHandlers,
  useCommonVisibilityConditions,
} from "./shared/PaymentSharedComponents";

const canShowApplePayButton = () => {
  try {
    return (
      window.location.protocol === "https:" &&
      Boolean(window.ApplePaySession?.canMakePayments?.())
    );
  } catch {
    return false;
  }
};

const WalletButtonSection = memo(
  ({ show, containerId, loading, ready, sessionLoading, loadingLabel }) => {
    if (!show) return null;

    const showLoading = !ready && (loading || sessionLoading);

    return (
      <Grid
        item
        xs={12}
        sx={
          !ready && !showLoading
            ? { height: 0, minHeight: 0, overflow: "hidden", mb: 0 }
            : undefined
        }
      >
        <Box
          sx={{
            position: "relative",
            minHeight: ready || showLoading ? 48 : 0,
            overflow: "hidden",
            borderRadius: "4px",
          }}
        >
          <Box
            id={containerId}
            sx={{
              minHeight: 48,
              width: "100%",
              visibility: ready ? "visible" : "hidden",
              pointerEvents: ready && !sessionLoading ? "auto" : "none",
            }}
          />
          {showLoading && (
            <Box
              sx={{
                position: "absolute",
                inset: 0,
                zIndex: 10,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                backgroundColor: "#ffffff",
                borderRadius: "4px",
              }}
            >
              <CircularProgress size={18} color="primary" />
              <Typography variant="body2" color="text.secondary">
                {loadingLabel}
              </Typography>
            </Box>
          )}
          <style>{`
            #${containerId} > div,
            #${containerId} .adyen-checkout__googlepay,
            #${containerId} .adyen-checkout__applepay,
            #${containerId} .gpay-button-fill {
              width: 100% !important;
              max-width: 100% !important;
            }
            #${containerId} button,
            #${containerId} .gpay-button,
            #${containerId} apple-pay-button {
              min-height: 48px !important;
            }
            #${containerId} .adyen-checkout__spinner,
            #${containerId} .loading-input__spinner {
              display: none !important;
            }
          `}</style>
        </Box>
      </Grid>
    );
  },
);

WalletButtonSection.displayName = "WalletButtonSection";

const AdyenPayments = memo(({ loaded, error, bId, oId, isWebView }) => {
  const tipInfoSettings = useTipInfoSettingsContext();
  const { isDriver, charges, orderType, business, setIsPaid, paidTip } =
    useContext(TOContext);
  const [completeAdyenSession] = useCompleteAdyenSessionMutation();
  const payingRef = useRef(false);
  const customerInfoRef = useRef(null);
  const [adyenPaying, setAdyenPaying] = useState(false);

  const isApplePayDisabledForBusiness = Boolean(
    business?.settings?.disableApplePay,
  );
  const isGooglePayDisabledForBusiness = Boolean(
    business?.settings?.disableGooglePay,
  );

  const {
    userInfo,
    errorCard,
    setErrorCard,
    tokenForward,
    setHasTokenForward,
    handleSubmit,
    handleCashSubmit,
    loadingPay,
  } = useCreditCard();

  useEffect(() => {
    if (userInfo.formState.isValid) {
      customerInfoRef.current = userInfo.getValues();
    }
  }, [userInfo, userInfo.formState.isValid]);

  const adyenTipInfo = useMemo(
    () => ({
      kitchen: (Number(charges?.tipInfo?.kitchen) || 0) + Number(paidTip || 0),
      driver: Number(charges?.tipInfo?.driver) || 0,
    }),
    [charges?.tipInfo?.driver, charges?.tipInfo?.kitchen, paidTip],
  );

  const finalizeAdyenPayment = useCallback(
    async ({ sessionId, sessionResult, resultCode }) => {
      if (payingRef.current || !sessionId || !sessionResult) {
        return false;
      }

      if (
        ["cancelled", "error", "refused"].includes(
          String(resultCode || "").toLowerCase(),
        )
      ) {
        setIsPaid(false);
        toast.error(`Payment failed: ${resultCode}.`);
        return false;
      }

      payingRef.current = true;
      setAdyenPaying(true);
      try {
        const customerInfo = customerInfoRef.current;
        const requestData = {
          orderId: oId,
          sessionId,
          sessionResult,
          tipInfo: adyenTipInfo,
          ...(customerInfo
            ? {
                customerInfo: {
                  email: customerInfo.email || "",
                  phoneNumber: customerInfo.phone || "",
                  firstName: customerInfo.firstName || "",
                  lastName: customerInfo.lastName || "",
                },
              }
            : {}),
        };

        localStorage.setItem(`${oId}-balanceDue`, charges?.balanceDue);
        localStorage.removeItem("cashPaid");

        const response = await completeAdyenSession(requestData);
        if (response?.error) {
          setIsPaid(false);
          toast.error(
            response.error?.data?.message ||
              "Payment failed. Please try again.",
          );
          payingRef.current = false;
          setAdyenPaying(false);
          return false;
        }

        const completion = response?.data?.data ?? response?.data;
        if (!completion?.pspReference) {
          setIsPaid(false);
          toast.error("Adyen payment could not be verified. Please try again.");
          payingRef.current = false;
          setAdyenPaying(false);
          return false;
        }

        setIsPaid(true);
        return true;
      } catch (paymentError) {
        setIsPaid(false);
        toast.error(
          paymentError?.message || "Payment failed. Please try again.",
        );
        payingRef.current = false;
        setAdyenPaying(false);
        return false;
      }
    },
    [charges?.balanceDue, adyenTipInfo, completeAdyenSession, oId, setIsPaid],
  );

  const googlePayEnabled = !isGooglePayDisabledForBusiness;
  const applePayEnabled = !isApplePayDisabledForBusiness;
  const walletsVisible = !isWebView && !isDriver;
  const canMountWallets =
    walletsVisible && (googlePayEnabled || applePayEnabled);

  const {
    adyenCardReady,
    adyenPaymentCompleted,
    adyenSession,
    adyenSessionResult,
    adyenStatus,
    adyenGooglePayReady,
    adyenApplePayReady,
    adyenGooglePayLoading,
    adyenApplePayLoading,
    adyenSubmitting,
    loadingAdyenSession,
    dropinContainerId,
    googlePayContainerId,
    applePayContainerId,
    submitAdyenPayment,
  } = useAdyenPayment({
    businessId: bId || business?.id,
    orderId: oId,
    total: charges?.balanceDue,
    tipInfo: adyenTipInfo,
    googlePayEnabled,
    applePayEnabled,
    canMountWallets,
    onPaymentCompleted: finalizeAdyenPayment,
  });

  const commonVisibilityConditions = useCommonVisibilityConditions(
    loaded,
    error,
    isDriver,
    tipInfoSettings,
    orderType,
  );

  const memoizedHandlers = useCommonHandlers(
    handleSubmit,
    handleCashSubmit,
    setErrorCard,
    setHasTokenForward,
  );

  const handleSubmitAdyen = useCallback(
    (values) => {
      customerInfoRef.current = values || null;

      if (adyenPaymentCompleted && adyenSession?.id && adyenSessionResult) {
        void finalizeAdyenPayment({
          sessionId: adyenSession.id,
          sessionResult: adyenSessionResult,
        });
        return;
      }

      submitAdyenPayment();
    },
    [
      adyenPaymentCompleted,
      adyenSession?.id,
      adyenSessionResult,
      finalizeAdyenPayment,
      submitAdyenPayment,
    ],
  );

  const visibilityConditions = useMemo(
    () => ({
      ...commonVisibilityConditions,
      showApplePayButton: walletsVisible && applePayEnabled,
      showGooglePayButton: walletsVisible && googlePayEnabled,
    }),
    [
      applePayEnabled,
      commonVisibilityConditions,
      googlePayEnabled,
      walletsVisible,
    ],
  );

  const creditCardProps = useMemo(
    () => ({
      isWebView,
      userInfo,
      loadingPay: loadingPay || adyenPaying || adyenSubmitting,
      errorCard,
      setErrorCard: memoizedHandlers.setErrorCard,
      tokenForward,
      setHasTokenForward: memoizedHandlers.setHasTokenForward,
      handleSubmit: memoizedHandlers.handleSubmit,
      handleSubmitStripe: undefined,
      handleSubmitAdyen,
      adyenCardReady,
      adyenPaymentCompleted,
      loadingAdyenSession,
      dropinContainerId,
      adyenStatus,
      isLoading: loadingAdyenSession || adyenPaying || adyenSubmitting,
    }),
    [
      adyenCardReady,
      adyenPaymentCompleted,
      adyenStatus,
      dropinContainerId,
      errorCard,
      handleSubmitAdyen,
      isWebView,
      loadingAdyenSession,
      loadingPay,
      adyenPaying,
      adyenSubmitting,
      memoizedHandlers,
      tokenForward,
      userInfo,
    ],
  );

  return (
    <>
      <PaymentHeaderSection
        isWebView={isWebView}
        bId={bId}
        oId={oId}
        showKitchenTips={visibilityConditions.showKitchenTips}
        showDriverTips={visibilityConditions.showDriverTips}
      />

      <Box sx={{ mt: isWebView ? 0 : 2 }}>
        <Grid container spacing={1}>
          <PaymentTypeHeader isWebView={isWebView} />

          <Grid item xs={12}>
            <PaymentMethodIcons />
          </Grid>

          <WalletButtonSection
            show={
              visibilityConditions.showApplePayButton &&
              canShowApplePayButton()
            }
            containerId={applePayContainerId}
            loading={adyenApplePayLoading}
            ready={adyenApplePayReady}
            sessionLoading={loadingAdyenSession}
            loadingLabel="Loading Apple Pay..."
          />

          <WalletButtonSection
            show={visibilityConditions.showGooglePayButton}
            containerId={googlePayContainerId}
            loading={adyenGooglePayLoading}
            ready={adyenGooglePayReady}
            sessionLoading={loadingAdyenSession}
            loadingLabel="Loading Google Pay..."
          />

          {((visibilityConditions.showApplePayButton &&
            canShowApplePayButton() &&
            (adyenApplePayReady ||
              adyenApplePayLoading ||
              loadingAdyenSession)) ||
            (visibilityConditions.showGooglePayButton &&
              (adyenGooglePayReady ||
                adyenGooglePayLoading ||
                loadingAdyenSession))) && (
            <Grid item xs={12}>
              <PaymentDivider />
            </Grid>
          )}

          <CashPaymentSection
            showDriverContent={visibilityConditions.showDriverContent}
            loadingPay={loadingPay}
            handleCashSubmit={memoizedHandlers.handleCashSubmit}
            oId={oId}
            showCashPayment={visibilityConditions.showCashPayment}
          />

          <Grid item xs={12}>
            {visibilityConditions.showCardPayment && (
              <TOCardPayment {...creditCardProps} />
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
});

AdyenPayments.displayName = "AdyenPayments";

export default AdyenPayments;
